import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, CheckCircle, AlertTriangle, XCircle, X } from 'lucide-react';

export type MessageType = 'error' | 'success' | 'warning';

interface MessageModalProps {
    isOpen: boolean;
    title: string;
    message: string;
    type?: MessageType;
    onClose: () => void;
    confirmLabel?: string;
}

const MessageModal: React.FC<MessageModalProps> = ({
    isOpen,
    title,
    message,
    type = 'error',
    onClose,
    confirmLabel = 'Dismiss'
}) => {
    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-charcoal/50 backdrop-blur-sm"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-white/50"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${type === 'error' ? 'bg-red-100 text-red-600' :
                                        type === 'success' ? 'bg-green-100 text-green-600' :
                                            'bg-amber-100 text-amber-600'
                                    }`}>
                                    {type === 'error' ? <AlertCircle className="w-6 h-6" /> :
                                        type === 'success' ? <CheckCircle className="w-6 h-6" /> :
                                            <AlertTriangle className="w-6 h-6" />}
                                </div>
                                <button
                                    onClick={onClose}
                                    className="text-slate-gray hover:text-charcoal transition-colors"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                            <h3 className="text-xl font-bold text-charcoal mb-2">{title}</h3>
                            <p className="text-slate-gray mb-6 whitespace-pre-line">{message}</p>
                            <button
                                onClick={onClose}
                                className={`w-full py-3 rounded-xl font-semibold text-white transition-all duration-300 ${type === 'error' ? 'bg-red-600 hover:bg-red-700' :
                                        type === 'success' ? 'bg-gradient-success hover:shadow-lg' :
                                            'bg-amber-600 hover:bg-amber-700'
                                    }`}
                            >
                                {confirmLabel}
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default MessageModal;
